import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  FiAlignCenter,
  FiAlignJustify,
  FiAlignLeft,
  FiAlignRight,
  FiBold,
  FiCode,
  FiImage,
  FiItalic,
  FiLink,
  FiList,
  FiMinus,
  FiRotateCcw,
  FiRotateCw,
  FiType,
  FiUnderline,
} from 'react-icons/fi'
import { BiCodeBlock } from 'react-icons/bi'
import { MdFormatClear } from 'react-icons/md'
import { RiDoubleQuotesL, RiListOrdered, RiMarkPenLine, RiStrikethrough2 } from 'react-icons/ri'

import RichEditor, { type EditorToolbarState, type RichEditorHandle } from '../../components/editor/RichEditor'
import { myBusinesses, updateBusiness } from '../../api/businessesReader'
import { uploadPeopleImage } from '../../api/uploads'
import BusinessAvatar from '../../components/businesses/BusinessAvatar'
import BusinessPixeImageEditor from '../../components/businesses/BusinessPixeImageEditor'
import { getToken } from '../../lib/auth'
import type { BusinessProfile } from '../../types/businesses'

const BLANK_HTML = '<p></p>'

export default function PageEditor() {
  const authed = Boolean(getToken())
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const richRef = useRef<RichEditorHandle | null>(null)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [business, setBusiness] = useState<BusinessProfile | null>(null)

  const [storyTitle, setStoryTitle] = useState('')
  const [storySubtitle, setStorySubtitle] = useState('')
  const [storyBody, setStoryBody] = useState(BLANK_HTML)
  const [portraitUrl, setPortraitUrl] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  const [coverUrl, setCoverUrl] = useState('')
  const [uploadingPortrait, setUploadingPortrait] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadingLandscape, setUploadingLandscape] = useState(false)
  const logoFileRef = useRef<HTMLInputElement | null>(null)
  const [editorKey, setEditorKey] = useState(0)
  const [device, setDevice] = useState<'mobile' | 'desktop'>('mobile')
  const [tb, setTb] = useState<EditorToolbarState>({
    bold: false,
    italic: false,
    underline: false,
    strike: false,
    code: false,
    highlight: false,
    link: false,
    h1: false,
    h2: false,
    h3: false,
    h4: false,
    bullet: false,
    ordered: false,
    quote: false,
    codeBlock: false,
    align: 'left',
    canUndo: false,
    canRedo: false,
  })

  useEffect(() => {
    if (!authed) {
      navigate('/login')
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    myBusinesses()
      .then((rows) => {
        if (cancelled) return
        const selected = rows.find((b) => b.slug === slug) || null
        if (!selected) {
          setError('Not authorized')
          return
        }
        setBusiness(selected)
        setStoryTitle(selected.story_title || '')
        setStorySubtitle(selected.story_subtitle || '')
        setStoryBody(selected.story_body || selected.story_rendered_html || BLANK_HTML)
        setPortraitUrl(selected.pixe_portrait_image_url || '')
        setLogoUrl(selected.logo_image_url || '')
        setCoverUrl(selected.cover_image_url || '')
        setEditorKey((k) => k + 1)
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || 'Failed to load business')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [authed, navigate, slug])

  const previewHtml = useMemo(() => {
    const html = String(storyBody || '').trim() || BLANK_HTML
    const purifier = (window as any).DOMPurify
    return purifier?.sanitize?.(html) ?? html
  }, [storyBody])

  const refreshToolbar = () => richRef.current?.refreshToolbarState()
  const syncEditor = () => setStoryBody(richRef.current?.getHTML() || BLANK_HTML)

  function toggleBold() { richRef.current?.toggleBold(); refreshToolbar() }
  function toggleItalic() { richRef.current?.toggleItalic(); refreshToolbar() }
  function toggleStrike() { richRef.current?.toggleStrike(); refreshToolbar() }
  function toggleCode() { richRef.current?.toggleCode(); refreshToolbar() }
  function toggleUnderline() { richRef.current?.toggleUnderline(); refreshToolbar() }
  function setH1() { richRef.current?.setHeading(1); refreshToolbar() }
  function setH2() { richRef.current?.setHeading(2); refreshToolbar() }
  function setH3() { richRef.current?.setHeading(3); refreshToolbar() }
  function setH4() { richRef.current?.setHeading(4); refreshToolbar() }
  function toggleBullet() { richRef.current?.toggleBulletList(); refreshToolbar() }
  function toggleOrdered() { richRef.current?.toggleOrderedList(); refreshToolbar() }
  function toggleQuote() { richRef.current?.toggleBlockquote(); refreshToolbar() }
  function toggleHighlight() { richRef.current?.toggleHighlight?.(); refreshToolbar() }
  function toggleCodeBlock() { richRef.current?.toggleCodeBlock(); refreshToolbar() }
  function insertDivider() { richRef.current?.insertDivider(); syncEditor(); refreshToolbar() }
  function alignLeft() { richRef.current?.setTextAlign('left'); refreshToolbar() }
  function alignCenter() { richRef.current?.setTextAlign('center'); refreshToolbar() }
  function alignRight() { richRef.current?.setTextAlign('right'); refreshToolbar() }
  function alignJustify() { richRef.current?.setTextAlign('justify'); refreshToolbar() }
  function undo() { richRef.current?.undo(); syncEditor(); refreshToolbar() }
  function redo() { richRef.current?.redo(); syncEditor(); refreshToolbar() }
  function clearFormatting() { richRef.current?.clearFormatting(); refreshToolbar() }

  function addLink() {
    const url = window.prompt('Enter URL (https://...)')?.trim()
    if (!url) return
    const hasSelection = !!richRef.current?.getSelectionText()
    if (hasSelection) richRef.current?.setLink(url)
    else richRef.current?.appendHTML(`<a href="${url}">${url}</a>`)
    syncEditor()
  }

  function addImage() {
    const url = window.prompt('Image URL (https://...)')?.trim()
    if (!url) return
    const alt = window.prompt('Alt text (optional)')?.trim() || ''
    richRef.current?.insertImage(url, alt)
    syncEditor()
  }

  async function cropSquareFile(file: File, size = 720): Promise<File> {
    const imageUrl = URL.createObjectURL(file)
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image()
        el.onload = () => resolve(el)
        el.onerror = reject
        el.src = imageUrl
      })
      const minEdge = Math.min(img.naturalWidth, img.naturalHeight)
      const sx = Math.floor((img.naturalWidth - minEdge) / 2)
      const sy = Math.floor((img.naturalHeight - minEdge) / 2)
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('Canvas unavailable')
      ctx.drawImage(img, sx, sy, minEdge, minEdge, 0, 0, size, size)
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92))
      if (!blob) throw new Error('Image crop failed')
      const baseName = (file.name || 'logo').replace(/\.[^.]+$/, '')
      return new File([blob], `${baseName}-square.jpg`, { type: 'image/jpeg' })
    } finally {
      URL.revokeObjectURL(imageUrl)
    }
  }

  async function onUploadLogo(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !business || uploadingLogo) return
    setUploadingLogo(true)
    setError(null)
    setMessage(null)
    try {
      const cropped = await cropSquareFile(file)
      const up = await uploadPeopleImage(cropped)
      const saved = await updateBusiness(business.slug, { logo_image_url: up.url })
      setBusiness(saved)
      setLogoUrl(saved.logo_image_url || up.url)
      setMessage('Logo updated')
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Logo upload failed')
    } finally {
      setUploadingLogo(false)
      if (logoFileRef.current) logoFileRef.current.value = ''
    }
  }

  async function onUploadPortrait(file: File) {
    if (!file || !business || uploadingPortrait) return
    setUploadingPortrait(true)
    setError(null)
    setMessage(null)
    try {
      const up = await uploadPeopleImage(file)
      const saved = await updateBusiness(business.slug, { pixe_portrait_image_url: up.url })
      setBusiness(saved)
      setPortraitUrl(saved.pixe_portrait_image_url || up.url)
      setMessage('Portrait image updated')
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Portrait upload failed')
    } finally {
      setUploadingPortrait(false)
    }
  }

  async function onUploadLandscape(file: File) {
    if (!file || !business || uploadingLandscape) return
    setUploadingLandscape(true)
    setError(null)
    setMessage(null)
    try {
      const up = await uploadPeopleImage(file)
      const saved = await updateBusiness(business.slug, { cover_image_url: up.url })
      setBusiness(saved)
      setCoverUrl(saved.cover_image_url || up.url)
      setMessage('Header image updated')
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Header upload failed')
    } finally {
      setUploadingLandscape(false)
    }
  }

  async function onSave() {
    if (!business || saving) return
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      const updated = await updateBusiness(business.slug, {
        story_title: storyTitle.trim(),
        story_subtitle: storySubtitle.trim(),
        story_body: storyBody,
        pixe_portrait_image_url: portraitUrl.trim(),
        logo_image_url: logoUrl.trim(),
        cover_image_url: coverUrl.trim(),
      })
      setBusiness(updated)
      setStoryBody(updated.story_body || BLANK_HTML)
      setPortraitUrl(updated.pixe_portrait_image_url || '')
      setLogoUrl(updated.logo_image_url || '')
      setCoverUrl(updated.cover_image_url || '')
      setMessage('Saved')
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      if (typeof detail === 'string' && detail) setError(detail)
      else setError(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (!authed) return null
  if (loading) return <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">Loading editor...</div>
  if (error === 'Not authorized') {
    return (
      <div className="rounded-xl border border-white/10 bg-white/5 p-5">
        <h1 className="text-xl font-extrabold">Not authorized</h1>
        <p className="mt-2 text-sm text-white/70">You do not have access to edit this business.</p>
      </div>
    )
  }
  if (!business) return <div className="text-sm text-red-300">{error || 'Business not found'}</div>

  return (
    <div className="min-h-screen">
      <main className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl font-extrabold">Edit Business Page</h1>
          <div className="flex items-center gap-2">
            <Link to={`/settings/pages/${business.slug}`} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">Back to Admin</Link>
            <Link to={`/b/${business.slug}`} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">View Public Page</Link>
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className={`rounded-lg px-3 py-2 text-sm font-semibold ${saving ? 'bg-white/10 text-white/50' : 'bg-brand text-white hover:bg-brand-600'}`}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {message ? <div className="mb-3 text-sm text-emerald-300">{message}</div> : null}
        {error && error !== 'Not authorized' ? <div className="mb-3 text-sm text-red-300">{error}</div> : null}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <section className="lg:col-span-7 space-y-4">
            <BusinessPixeImageEditor
              portraitUrl={portraitUrl}
              landscapeUrl={coverUrl}
              uploadingPortrait={uploadingPortrait}
              uploadingLandscape={uploadingLandscape}
              onUploadPortrait={onUploadPortrait}
              onUploadLandscape={onUploadLandscape}
              onPortraitUrlChange={setPortraitUrl}
              onLandscapeUrlChange={setCoverUrl}
              onClearPortrait={() => setPortraitUrl('')}
              onClearLandscape={() => setCoverUrl('')}
            />

            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="mb-3 text-sm font-semibold">Story</div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <input value={storyTitle} onChange={(e) => setStoryTitle(e.target.value)} placeholder="Story title" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
                <input value={storySubtitle} onChange={(e) => setStorySubtitle(e.target.value)} placeholder="Story subtitle" className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand" />
              </div>
            </div>

            <div className="rounded-xl border border-white/10 p-3 bg-black/30">
              <div className="flex flex-wrap items-center gap-2">
                <button title="Bold" onClick={toggleBold} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.bold ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiBold /></button>
                <button title="Italic" onClick={toggleItalic} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.italic ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiItalic /></button>
                <button title="Underline" onClick={toggleUnderline} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.underline ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiUnderline /></button>
                <button title="Strike" onClick={toggleStrike} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.strike ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><RiStrikethrough2 /></button>
                <button title="Code" onClick={toggleCode} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.code ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiCode /></button>
                <button title="Highlight" onClick={toggleHighlight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.highlight ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><RiMarkPenLine /></button>
                <button title="H1" onClick={setH1} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h1 ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiType /><span className="text-[11px]">H1</span></button>
                <button title="H2" onClick={setH2} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h2 ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiType /><span className="text-[11px]">H2</span></button>
                <button title="H3" onClick={setH3} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h3 ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiType /><span className="text-[11px]">H3</span></button>
                <button title="H4" onClick={setH4} className={`inline-flex items-center gap-1 px-2.5 h-8 rounded-md border ${tb.h4 ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiType /><span className="text-[11px]">H4</span></button>
                <button title="Bullets" onClick={toggleBullet} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.bullet ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiList /></button>
                <button title="Numbered" onClick={toggleOrdered} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.ordered ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><RiListOrdered /></button>
                <button title="Quote" onClick={toggleQuote} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.quote ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><RiDoubleQuotesL /></button>
                <button title="Code block" onClick={toggleCodeBlock} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.codeBlock ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><BiCodeBlock /></button>
                <button title="Divider" onClick={insertDivider} className="inline-flex items-center justify-center h-8 w-8 rounded-md border bg-white/5 hover:bg-white/10 border-white/10"><FiMinus /></button>
                <button title="Align left" onClick={alignLeft} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'left' ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiAlignLeft /></button>
                <button title="Align center" onClick={alignCenter} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'center' ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiAlignCenter /></button>
                <button title="Align right" onClick={alignRight} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'right' ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiAlignRight /></button>
                <button title="Justify" onClick={alignJustify} className={`inline-flex items-center justify-center h-8 w-8 rounded-md border ${tb.align === 'justify' ? 'bg-brand/60 border-brand/40' : 'bg-white/5 hover:bg-white/10 border-white/10'}`}><FiAlignJustify /></button>
                <button title="Add link" onClick={addLink} className="inline-flex items-center justify-center h-8 w-8 rounded-md border bg-white/5 hover:bg-white/10 border-white/10"><FiLink /></button>
                <button title="Add image" onClick={addImage} className="inline-flex items-center justify-center h-8 w-8 rounded-md border bg-white/5 hover:bg-white/10 border-white/10"><FiImage /></button>
                <button title="Undo" onClick={undo} className="inline-flex items-center justify-center h-8 w-8 rounded-md border bg-white/5 hover:bg-white/10 border-white/10"><FiRotateCcw /></button>
                <button title="Redo" onClick={redo} className="inline-flex items-center justify-center h-8 w-8 rounded-md border bg-white/5 hover:bg-white/10 border-white/10"><FiRotateCw /></button>
                <button title="Clear formatting" onClick={clearFormatting} className="inline-flex items-center justify-center h-8 w-8 rounded-md border bg-white/5 hover:bg-white/10 border-white/10"><MdFormatClear /></button>
              </div>
            </div>

            <RichEditor
              key={editorKey}
              ref={richRef}
              initialHTML={storyBody || BLANK_HTML}
              onChange={(html) => setStoryBody(html || BLANK_HTML)}
              onToolbarState={setTb}
              className="rounded-xl border border-white/10 p-2 bg-white/5"
            />
          </section>

          <aside className="lg:col-span-5">
            <div className="mb-4 rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="mb-3 text-sm font-semibold">Avatar</div>
              <div className="mb-3 flex items-center gap-3">
                <BusinessAvatar name={business.name} logoImageUrl={logoUrl} size={64} />
                <div className="flex items-center gap-2">
                  <input ref={logoFileRef} type="file" accept="image/*" onChange={onUploadLogo} className="hidden" />
                  <button
                    type="button"
                    onClick={() => logoFileRef.current?.click()}
                    disabled={uploadingLogo}
                    className={`rounded-lg border border-white/15 px-3 py-2 text-xs font-semibold ${uploadingLogo ? 'bg-white/10 text-white/50 cursor-not-allowed' : 'bg-white/5 hover:bg-white/10'}`}
                  >
                    {uploadingLogo ? 'Uploading...' : 'Upload new'}
                  </button>
                </div>
              </div>
              <input
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https:// avatar URL"
                className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
              />
            </div>

            <div className="mb-2 flex items-center justify-end">
              <div className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
                <button onClick={() => setDevice('mobile')} className={`px-2 py-1 text-xs rounded-full ${device === 'mobile' ? 'bg-white/10' : ''}`}>Mobile</button>
                <button onClick={() => setDevice('desktop')} className={`px-2 py-1 text-xs rounded-full ${device === 'desktop' ? 'bg-white/10' : ''}`}>Desktop</button>
              </div>
            </div>
            <div className={`${device === 'mobile' ? 'mx-auto w-[360px] md:w-[420px]' : ''} rounded-xl border border-white/10 overflow-hidden mb-4`}>
              <div className="relative aspect-[16/9]">
                {coverUrl || portraitUrl ? <img src={coverUrl || portraitUrl} alt="Cover" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 bg-white/5" />}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
                <div className="absolute left-4 bottom-4 flex items-center gap-3">
                  <BusinessAvatar name={business.name} logoImageUrl={logoUrl} size={48} />
                  <div>
                    <div className="text-white font-extrabold">{business.name}</div>
                    <div className="text-white/80 text-xs">{business.category}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className={`${device === 'mobile' ? 'mx-auto w-[360px] md:w-[420px]' : ''} rounded-xl border border-white/10 p-4`}>
              <div className="font-semibold mb-2">Story preview</div>
              {storyTitle ? <div className="mb-1 text-lg font-extrabold text-white">{storyTitle}</div> : null}
              {storySubtitle ? <div className="mb-3 text-white/75 text-sm">{storySubtitle}</div> : null}
              <div className="tiptap-mag prose prose-invert prose-base max-w-none prose-p:my-2 prose-li:my-1" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </aside>
        </div>
      </main>
    </div>
  )
}
